import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import type { IPayrollSheetApprovalAccessKey } from "@/payroll/payroll.d";
import { ApprovalMark } from "./approval-mark";
import { ApprovalToggle } from "./approval-toggle";
import { APPROVAL_GATES } from "./approval-types";
import type { ApprovalPanelProps } from "./types";
import { getSheetSummary } from "./utils";

const GATE_COLUMN_CLASSNAME =
  "flex w-[6.75rem] shrink-0 items-center justify-end";

/**
 * Sign-off across the whole period — one approval unit per row and one gate per
 * column, so the workbook's state reads as a compact matrix.
 */
export const PeriodApprovalPanel = ({
  accessKeys,
  pendingApproval,
  onApprovalChange,
}: ApprovalPanelProps & { accessKeys: IPayrollSheetApprovalAccessKey[] }) => {
  const hasUnauthorized = accessKeys.some((key) => !key.approval_authorized);

  return (
    <Stack gap="none" align="stretch" fullWidth className="min-w-0">
      <Stack
        gap="sm"
        align="stretch"
        fullWidth
        className="border-b border-stroke-tertiary px-4 py-3"
      >
        <Typography variant="subtitleW3">
          Approvals for this pay period
        </Typography>
        <Stack direction="row" align="center" gap="sm" fullWidth>
          <span className="min-w-0 flex-1" />
          {APPROVAL_GATES.map((gate) => (
            <span
              key={gate.type}
              className={cn(GATE_COLUMN_CLASSNAME, "gap-1")}
            >
              <gate.Icon
                aria-hidden="true"
                className="h-3.5 w-3.5 shrink-0 text-tertiary-text"
              />
              <Typography
                variant="captionW1"
                textColor="secondary"
                className="whitespace-nowrap"
              >
                {gate.label}
              </Typography>
            </span>
          ))}
        </Stack>
      </Stack>

      <Stack
        gap="none"
        align="stretch"
        fullWidth
        className="max-h-80 overflow-y-auto"
      >
        {accessKeys.map((accessKey) => {
          const sheetSummary = getSheetSummary(accessKey);

          return (
            <Stack
              key={accessKey.id}
              direction="row"
              align="center"
              gap="sm"
              fullWidth
              className={cn(
                "border-b border-stroke-tertiary px-4 py-2.5 last:border-b-0",
                "transition-colors hover:bg-surface-secondary",
              )}
            >
              <Stack
                direction="row"
                align="center"
                gap="sm"
                className="min-w-0 flex-1"
              >
                <ApprovalMark accessKey={accessKey} />
                <Stack gap="none" align="stretch" fullWidth className="min-w-0">
                  <Typography
                    variant="bodyW3"
                    className="truncate font-semibold"
                  >
                    {accessKey.name}
                  </Typography>
                  {sheetSummary && (
                    <Typography
                      variant="captionW1"
                      textColor="tertiary"
                      className="truncate"
                    >
                      {sheetSummary}
                    </Typography>
                  )}
                </Stack>
              </Stack>

              {APPROVAL_GATES.map((gate) => (
                <span key={gate.type} className={GATE_COLUMN_CLASSNAME}>
                  <ApprovalToggle
                    gate={gate}
                    accessKey={accessKey}
                    isBusy={Boolean(pendingApproval)}
                    isPending={
                      pendingApproval?.accessKeyId === accessKey.id &&
                      pendingApproval.approvalType === gate.type
                    }
                    onChange={
                      onApprovalChange
                        ? (enabled) =>
                            onApprovalChange(accessKey, gate.type, enabled)
                        : undefined
                    }
                  />
                </span>
              ))}
            </Stack>
          );
        })}
      </Stack>

      <Typography
        variant="captionW1"
        textColor="tertiary"
        className="border-t border-stroke-tertiary px-4 py-2.5"
      >
        {hasUnauthorized
          ? "Greyed out switches are sheets you can see but can't approve."
          : "Sheets in the same group are approved together."}
      </Typography>
    </Stack>
  );
};
